import React, { useMemo, useState, useCallback, memo } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import { 
    Clock, 
    Download, 
    Layers,
    Activity,
    TrendingUp,
    TrendingDown,
    Minus,
} from 'lucide-react'
import { useMarketStore } from '../../services/store'
import { useSettingsStore } from '../../stores/settingsStore'
import { getStrikeStep } from '../../lib/analysis-utils'
import { formatOI, formatVolume } from '../../lib/data-utils'
import { formatISTTime } from '../../lib/date-utils'
import { formatIndianScale } from '../../lib/table-utils'
import { ColumnSelector, type ColumnConfig } from '../ColumnSelector'
import { 
    INTRADAY_HISTORY_COLUMNS, 
    ITM_OTM_HISTORY_COLUMNS, 
    ABOVE_BELOW_HISTORY_COLUMNS 
} from '../../config/columns'
import { getCanonicalSymbol } from '../../config/constants'
export { 
    INTRADAY_HISTORY_COLUMNS, 
    ITM_OTM_HISTORY_COLUMNS, 
    ABOVE_BELOW_HISTORY_COLUMNS 
}
import { DataChip } from '../ui/ghost-table/DataChip'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
import { cn } from '../../lib/utils'

export type HistoryTableMode = 'intraday_history' | 'itm_otm_history' | 'above_below_history'
export type IntradaySubTab = 'All' | 'ITM' | 'OTM'
export type ItmOtmMetric = 'Volume' | 'OI' | 'Chng in OI'
export type DensityMode = 'compact' | 'standard'

interface IntradayHistoryTableProps {
    symbol: string
    expiry?: string
    marketData?: any
    className?: string
}

const TIME_INTERVALS = [
    { label: 'ALL', value: 0 },
    { label: '1m', value: 1 },
    { label: '2m', value: 2 },
    { label: '3m', value: 3 },
    { label: '5m', value: 5 },
    { label: '10m', value: 10 },
    { label: '15m', value: 15 },
    { label: '30m', value: 30 },
]

const EMPTY_HISTORY: any[] = []

export const IntradayHistoryTable: React.FC<IntradayHistoryTableProps> = ({
    symbol,
    expiry,
    marketData,
    className,
}) => {
    // 1. Storage & Preferences State
    const storagePrefix = `home_intraday_hist_${symbol}`

    // Mode Switcher: Intraday History | ITM / OTM History | Above / Below History
    const [historyMode, setHistoryMode] = useState<HistoryTableMode>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem(`${storagePrefix}_mode`) as HistoryTableMode) || 'intraday_history'
        }
        return 'intraday_history'
    })

    const handleHistoryModeChange = (mode: HistoryTableMode) => {
        setHistoryMode(mode)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storagePrefix}_mode`, mode)
        }
    }

    // SubTab for Intraday History mode (All | ITM | OTM)
    const [subTab, setSubTab] = useState<IntradaySubTab>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem(`${storagePrefix}_subTab`) as IntradaySubTab) || 'All'
        }
        return 'All'
    })

    // Metric Switcher for ITM/OTM History mode (Volume | OI | Chng in OI)
    const [itmOtmMetric, setItmOtmMetric] = useState<ItmOtmMetric>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem(`${storagePrefix}_itmOtmMetric`) as ItmOtmMetric) || 'OI'
        }
        return 'OI'
    })

    const handleItmOtmMetricChange = (metric: ItmOtmMetric) => {
        setItmOtmMetric(metric)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storagePrefix}_itmOtmMetric`, metric)
        }
    }

    const [includeAtm, setIncludeAtm] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem(`${storagePrefix}_includeAtm`) !== 'false'
        }
        return true
    })

    const [intervalMinutes, setIntervalMinutes] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            return parseInt(localStorage.getItem(`${storagePrefix}_interval`) || '1', 10)
        }
        return 1
    })

    const [density, setDensity] = useState<DensityMode>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem(`${storagePrefix}_density`) as DensityMode) || 'compact'
        }
        return 'compact'
    })

    const handleDensityChange = (d: DensityMode) => {
        setDensity(d)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storagePrefix}_density`, d)
        }
    }

    // Column Visibility State per Mode
    const [intradayColumns, setIntradayColumns] = useState<ColumnConfig[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storagePrefix}_columns`)
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    return INTRADAY_HISTORY_COLUMNS.map(col => {
                        const match = parsed.find((p: any) => p.id === col.id)
                        return match ? { ...col, visible: match.visible } : col
                    })
                } catch {}
            }
        }
        return INTRADAY_HISTORY_COLUMNS
    })

    const [itmOtmColumns, setItmOtmColumns] = useState<ColumnConfig[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storagePrefix}_itm_otm_columns`)
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    return ITM_OTM_HISTORY_COLUMNS.map(col => {
                        const match = parsed.find((p: any) => p.id === col.id)
                        return match ? { ...col, visible: match.visible } : col
                    })
                } catch {}
            }
        }
        return ITM_OTM_HISTORY_COLUMNS
    })

    const [aboveBelowColumns, setAboveBelowColumns] = useState<ColumnConfig[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`${storagePrefix}_above_below_columns`)
            if (saved) {
                try {
                    const parsed = JSON.parse(saved)
                    return ABOVE_BELOW_HISTORY_COLUMNS.map(col => {
                        const match = parsed.find((p: any) => p.id === col.id)
                        return match ? { ...col, visible: match.visible } : col
                    })
                } catch {}
            }
        }
        return ABOVE_BELOW_HISTORY_COLUMNS
    })

    const columns = historyMode === 'intraday_history' 
        ? intradayColumns 
        : historyMode === 'itm_otm_history' 
        ? itmOtmColumns 
        : aboveBelowColumns

    const isVisible = useCallback((id: string) => {
        const col = columns.find(c => c.id === id)
        return col ? col.visible : true
    }, [columns])

    const handleColumnToggle = useCallback((id: string) => {
        if (historyMode === 'intraday_history') {
            setIntradayColumns(prev => {
                const next = prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c)
                if (typeof window !== 'undefined') {
                    localStorage.setItem(`${storagePrefix}_columns`, JSON.stringify(next))
                }
                return next
            })
        } else if (historyMode === 'itm_otm_history') {
            setItmOtmColumns(prev => {
                const next = prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c)
                if (typeof window !== 'undefined') {
                    localStorage.setItem(`${storagePrefix}_itm_otm_columns`, JSON.stringify(next))
                }
                return next
            })
        } else {
            setAboveBelowColumns(prev => {
                const next = prev.map(c => c.id === id ? { ...c, visible: !c.visible } : c)
                if (typeof window !== 'undefined') {
                    localStorage.setItem(`${storagePrefix}_above_below_columns`, JSON.stringify(next))
                }
                return next
            })
        }
    }, [historyMode, storagePrefix])

    const handleSubTabChange = (tab: IntradaySubTab) => {
        setSubTab(tab)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storagePrefix}_subTab`, tab)
        }
    }

    const handleAtmToggle = () => {
        const next = !includeAtm
        setIncludeAtm(next)
        if (typeof window !== 'undefined') {
            localStorage.setItem(`${storagePrefix}_includeAtm`, next.toString())
        }
    }

    // 2. Data Sources
    const strikeRange = useSettingsStore(s => s.range || 11)
    const inspectionSource = useSettingsStore(s => s.inspectionSource)

    const rawHistory = useMarketStore(
        useCallback((state) => {
            const canonicalSym = (getCanonicalSymbol(symbol) || symbol) as string
            const sClean = (symbol || '').replace(/[^A-Z0-9]/g, '')

            // Check preferred bucket based on inspectionSource
            let data: any[] | undefined
            if (inspectionSource === 'HistoricalSnapshot') {
                data = state.historicalHistory?.[symbol] || state.historicalHistory?.[canonicalSym] || state.historicalHistory?.[sClean]
            } else if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') {
                data = state.rangeHistory?.[symbol] || state.rangeHistory?.[canonicalSym] || state.rangeHistory?.[sClean]
            } else {
                data = state.intradayHistory?.[symbol] || state.intradayHistory?.[canonicalSym] || state.intradayHistory?.[sClean]
            }

            // Fallback across all buckets if the primary is empty
            if (!data || data.length === 0) {
                data = state.historicalHistory?.[symbol] || state.historicalHistory?.[canonicalSym] || state.historicalHistory?.[sClean] ||
                       state.intradayHistory?.[symbol] || state.intradayHistory?.[canonicalSym] || state.intradayHistory?.[sClean] ||
                       state.rangeHistory?.[symbol] || state.rangeHistory?.[canonicalSym] || state.rangeHistory?.[sClean]
            }

            return Array.isArray(data) && data.length > 0 ? data : EMPTY_HISTORY
        }, [symbol, inspectionSource])
    )

    // 3. Compute Live Row (Dedicated Live Snapshot)
    const liveRow = useMemo(() => {
        if (!marketData) return null
        let row: any = null

        if (historyMode === 'intraday_history') {
            row = computeSnapshotMetrics(marketData, symbol, expiry, strikeRange, subTab, includeAtm)
            if (row.ceTotalOI === 0 && row.peTotalOI === 0 && row.ceTotalVolume === 0) return null
        } else if (historyMode === 'itm_otm_history') {
            row = computeItmOtmSnapshot(marketData, symbol, expiry, strikeRange, itmOtmMetric, includeAtm)
            if (row.itmCall === 0 && row.itmPut === 0 && row.otmCall === 0 && row.otmPut === 0) return null
        } else {
            row = computeAboveBelowSnapshot(marketData, symbol, expiry, strikeRange, includeAtm)
            if (row.belowCombOI === 0 && row.aboveCombOI === 0 && row.belowCombVol === 0) return null
        }

        row.isLive = true
        return row
    }, [marketData, symbol, expiry, strikeRange, subTab, includeAtm, historyMode, itmOtmMetric])

    // 4. Compute Historical Rows
    const historyRows = useMemo(() => {
        if (!rawHistory || rawHistory.length === 0) return []

        // Step 1: Deduplicate snapshots by minute — keeps ONLY the last latest backup for each minute
        const dedupedSnapshots = deduplicateSnapshotsByMinute(rawHistory)

        // Step 2: Apply Time Interval Filter to history (if user selected > 1 min interval)
        let filteredSnapshots = dedupedSnapshots
        if (intervalMinutes > 1) {
            const intervalMs = intervalMinutes * 60 * 1000
            const buckets: typeof rawHistory = []
            let lastBucketTime = 0

            for (const snap of dedupedSnapshots) {
                const rawT = snap.timestamp || snap.timestampIST || snap.time || snap.TimeStamp || 0
                const t = typeof rawT === 'number' ? (rawT < 1e11 ? rawT * 1000 : rawT) : new Date(rawT).getTime()
                if (!lastBucketTime || (t && t - lastBucketTime >= intervalMs - 5000)) {
                    buckets.push(snap)
                    lastBucketTime = t
                }
            }
            filteredSnapshots = buckets
        }

        if (historyMode === 'intraday_history') {
            return filteredSnapshots.map((snap) => {
                const histRow = computeSnapshotMetrics(snap, symbol, expiry, strikeRange, subTab, includeAtm)
                histRow.isLive = false
                return histRow
            }).reverse()
        } else if (historyMode === 'itm_otm_history') {
            return filteredSnapshots.map((snap) => {
                const histRow = computeItmOtmSnapshot(snap, symbol, expiry, strikeRange, itmOtmMetric, includeAtm)
                histRow.isLive = false
                return histRow
            }).reverse()
        } else {
            return filteredSnapshots.map((snap) => {
                const histRow = computeAboveBelowSnapshot(snap, symbol, expiry, strikeRange, includeAtm)
                histRow.isLive = false
                return histRow
            }).reverse()
        }
    }, [rawHistory, symbol, expiry, strikeRange, subTab, includeAtm, intervalMinutes, historyMode, itmOtmMetric])

    // Export to CSV / Excel
    const handleExport = () => {
        const allRows = liveRow ? [liveRow, ...historyRows] : historyRows
        if (allRows.length === 0) return

        if (historyMode === 'intraday_history') {
            const headers = [
                'Time', 
                'CE Total OI', 'PE Total OI', 
                'CE Total Change OI', 'PE Total Change OI', 
                'CE Total Volume', 'PE Total Volume', 
                'Total Combined OI', 'Total Combined Volume', 
                'Total Comb OI Chg', 'Total Comb OI Chg%', 
                'Total Vol Diff', 'Vol Diff %',
                'Total OI Diff', 'OI Diff %',
                'CE Total Vol-OI', 'PE Total Vol-OI', 
                'CE Total Vol-OI-Chng', 'PE Total Vol-OI-Chng', 
                'Index LTP'
            ]

            const csvContent = [
                headers.join(','),
                ...allRows.map(r => [
                    `"${r.isLive ? 'LIVE (' + r.timeStr + ')' : r.timeStr}"`,
                    r.ceTotalOI,
                    r.peTotalOI,
                    r.ceTotalChangeOI,
                    r.peTotalChangeOI,
                    r.ceTotalVolume,
                    r.peTotalVolume,
                    r.totalCombinedOI,
                    r.totalCombinedVolume,
                    r.totalCombOIChg,
                    `"${r.totalCombOIChgP >= 0 ? '+' : ''}${r.totalCombOIChgP.toFixed(2)}%"`,
                    r.totalVolDiff,
                    `"${r.totalVolDiffP >= 0 ? '+' : ''}${(r.totalVolDiffP || 0).toFixed(2)}%"`,
                    r.totalOIDiff,
                    `"${r.totalOIDiffP >= 0 ? '+' : ''}${(r.totalOIDiffP || 0).toFixed(2)}%"`,
                    r.ceTotalVolOI,
                    r.peTotalVolOI,
                    r.ceTotalVolOIChng,
                    r.peTotalVolOIChng,
                    r.indexLtp
                ].join(','))
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute('download', `${symbol}_Intraday_History_${subTab}_${new Date().toISOString().split('T')[0]}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } else if (historyMode === 'itm_otm_history') {
            const headers = [
                'Time', 'V1 Strike', 'ITM Call', 'ITM Put', 'ITM Net', 'ITM Net %', 'OTM Call', 'OTM Put', 'OTM Net', 'OTM Net %', 'Final Net', 'Final Net %', 'Index LTP'
            ]

            const csvContent = [
                headers.join(','),
                ...allRows.map(r => [
                    `"${r.isLive ? 'LIVE (' + r.timeStr + ')' : r.timeStr}"`,
                    r.atmStrike,
                    r.itmCall,
                    r.itmPut,
                    r.itmNet,
                    `"${r.itmNetP >= 0 ? '+' : ''}${(r.itmNetP || 0).toFixed(2)}%"`,
                    r.otmCall,
                    r.otmPut,
                    r.otmNet,
                    `"${r.otmNetP >= 0 ? '+' : ''}${(r.otmNetP || 0).toFixed(2)}%"`,
                    r.finalNet,
                    `"${r.finalNetP >= 0 ? '+' : ''}${(r.finalNetP || 0).toFixed(2)}%"`,
                    r.indexLtp
                ].join(','))
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute('download', `${symbol}_ITM_OTM_History_${itmOtmMetric}_${new Date().toISOString().split('T')[0]}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } else {
            const headers = [
                'Time', 'ATM Strike', 'Index LTP',
                'Below Comb OI', 'Above Comb OI', 'Net OI (B - A)', 'Net OI %',
                'Below Comb Vol', 'Above Comb Vol', 'Net Vol (B - A)', 'Net Vol %',
                'Below Comb OI Chg', 'Above Comb OI Chg', 'Net OI Chg (B - A)', 'Net OI Chg %'
            ]

            const csvContent = [
                headers.join(','),
                ...allRows.map(r => [
                    `"${r.isLive ? 'LIVE (' + r.timeStr + ')' : r.timeStr}"`,
                    r.atmStrike,
                    r.indexLtp,
                    r.belowCombOI,
                    r.aboveCombOI,
                    r.netCombOI,
                    `"${r.netCombOIP >= 0 ? '+' : ''}${(r.netCombOIP || 0).toFixed(2)}%"`,
                    r.belowCombVol,
                    r.aboveCombVol,
                    r.netCombVol,
                    `"${r.netCombVolP >= 0 ? '+' : ''}${(r.netCombVolP || 0).toFixed(2)}%"`,
                    r.belowCombOIChg,
                    r.aboveCombOIChg,
                    r.netCombOIChg,
                    `"${r.netCombOIChgP >= 0 ? '+' : ''}${(r.netCombOIChgP || 0).toFixed(2)}%"`
                ].join(','))
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute('download', `${symbol}_Above_Below_History_${new Date().toISOString().split('T')[0]}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    return (
        <div className={cn("bg-white dark:bg-slate-900 rounded-[22px] shadow-sm border border-slate-200/80 dark:border-slate-800 overflow-hidden transition-all duration-300", className)}>
            {/* Header Toolbar */}
            <div className="px-4 py-3 flex flex-wrap gap-y-3 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md transition-all">
                {/* Left: Title & Mode Switcher */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-gradient-to-br from-indigo-500/10 to-indigo-600/20 dark:from-indigo-500/20 dark:to-indigo-600/30 rounded-xl text-indigo-600 dark:text-indigo-400 shadow-2xs ring-1 ring-indigo-200/50 dark:ring-indigo-800/50">
                            <Clock size={18} strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-base font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                    Intraday History
                                </h2>
                                <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-200/60 dark:border-slate-700/60">
                                    {historyRows.length} snapshots
                                </span>
                            </div>
                            <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">
                                {symbol} • {expiry || 'Expiry'} • {includeAtm ? (historyMode === 'above_below_history' ? 'ATM Added to Below' : 'ATM Included') : 'ATM Excluded'}
                            </p>
                        </div>
                    </div>

                    {/* Primary History Mode Switcher: Intraday History | ITM / OTM History | Above / Below History */}
                    <div className="flex items-center p-0.5 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl ring-1 ring-inset ring-slate-200/60 dark:ring-slate-700/60 shadow-2xs">
                        {[
                            { id: 'intraday_history', label: 'Intraday History' },
                            { id: 'itm_otm_history', label: 'ITM / OTM History' },
                            { id: 'above_below_history', label: 'Above / Below History' },
                        ].map((m) => {
                            const isActive = historyMode === m.id
                            return (
                                <button
                                    key={m.id}
                                    onClick={() => handleHistoryModeChange(m.id as HistoryTableMode)}
                                    className={cn(
                                        "px-2.5 py-1 rounded-lg text-xs font-black tracking-tight transition-all duration-200 select-none outline-none cursor-pointer",
                                        isActive
                                            ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs ring-1 ring-slate-200/60 dark:ring-slate-600 scale-[1.01]"
                                            : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                    )}
                                >
                                    {m.label}
                                </button>
                            )
                        })}
                    </div>
                </div>

                {/* Center: Contextual Sub-Tabs / Metric Toggle + ATM Toggle + Interval + Density */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Intraday History: Sub Tabs (All | ITM | OTM) */}
                    {historyMode === 'intraday_history' && (
                        <div className="flex items-center p-0.5 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl ring-1 ring-inset ring-slate-200/60 dark:ring-slate-700/60 shadow-2xs">
                            {(['All', 'ITM', 'OTM'] as const).map((tab) => {
                                const isActive = subTab === tab
                                return (
                                    <button
                                        key={tab}
                                        onClick={() => handleSubTabChange(tab)}
                                        className={cn(
                                            "px-2.5 py-1 rounded-lg text-xs font-black tracking-tight transition-all duration-200 select-none outline-none cursor-pointer",
                                            isActive
                                                ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs ring-1 ring-slate-200/60 dark:ring-slate-600"
                                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                        )}
                                    >
                                        {tab}
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    {/* ITM / OTM History: Metric Switcher (Volume | OI | Chng in OI) */}
                    {historyMode === 'itm_otm_history' && (
                        <div className="flex items-center p-0.5 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl ring-1 ring-inset ring-slate-200/60 dark:ring-slate-700/60 shadow-2xs">
                            {(['OI', 'Volume', 'Chng in OI'] as const).map((m) => {
                                const isActive = itmOtmMetric === m
                                return (
                                    <button
                                        key={m}
                                        onClick={() => handleItmOtmMetricChange(m as ItmOtmMetric)}
                                        className={cn(
                                            "px-2.5 py-1 rounded-lg text-xs font-black tracking-tight transition-all duration-200 select-none outline-none cursor-pointer",
                                            isActive
                                                ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-xs ring-1 ring-slate-200/60 dark:ring-slate-600"
                                                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                        )}
                                    >
                                        {m}
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    {/* Above / Below History: Info Badge */}
                    {historyMode === 'above_below_history' && (
                        <div className="hidden sm:flex items-center px-2.5 py-1 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/50 text-[11px] font-black text-indigo-700 dark:text-indigo-300 shadow-2xs">
                            Below (-) Above Flow
                        </div>
                    )}

                    {/* ATM ON/OFF Toggle */}
                    <button
                        onClick={handleAtmToggle}
                        className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-black tracking-tight border transition-all duration-200 shadow-2xs cursor-pointer",
                            includeAtm
                                ? "bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border-purple-200/80 dark:border-purple-800/60 ring-1 ring-purple-300/30"
                                : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 opacity-80"
                        )}
                        title={historyMode === 'above_below_history' 
                            ? (includeAtm ? "ATM ON: Added to Below Strike sum" : "ATM OFF: Excluded from calculations")
                            : "Toggle inclusion of At-The-Money (ATM) strike"}
                    >
                        <span className={cn("w-1.5 h-1.5 rounded-full", includeAtm ? "bg-purple-500 animate-pulse" : "bg-slate-400")} />
                        <span>ATM {includeAtm ? 'ON' : 'OFF'}</span>
                    </button>

                    {/* Time Interval Dropdown */}
                    <div className="flex items-center gap-1 bg-slate-100/90 dark:bg-slate-800/90 px-2 py-1 rounded-xl ring-1 ring-inset ring-slate-200/60 dark:ring-slate-700/60">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">Step:</span>
                        <select
                            value={intervalMinutes}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10)
                                setIntervalMinutes(val)
                                if (typeof window !== 'undefined') localStorage.setItem(`${storagePrefix}_interval`, val.toString())
                            }}
                            className="bg-transparent text-xs font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
                        >
                            {TIME_INTERVALS.map((t) => (
                                <option key={t.label} value={t.value} className="dark:bg-slate-800">
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Density Switcher: Compact | Standard */}
                    <div className="flex items-center p-0.5 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl ring-1 ring-inset ring-slate-200/60 dark:ring-slate-700/60">
                        <button
                            onClick={() => handleDensityChange('compact')}
                            className={cn(
                                "px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer",
                                density === 'compact'
                                    ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-2xs ring-1 ring-slate-200/60 dark:ring-slate-600"
                                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            )}
                            title="Ultra-compact 36px row height"
                        >
                            Compact
                        </button>
                        <button
                            onClick={() => handleDensityChange('standard')}
                            className={cn(
                                "px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer",
                                density === 'standard'
                                    ? "bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-2xs ring-1 ring-slate-200/60 dark:ring-slate-600"
                                    : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                            )}
                            title="Standard 44px row height"
                        >
                            Standard
                        </button>
                    </div>
                </div>

                {/* Right: Actions (Columns, Export) */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700/80 shadow-2xs hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all cursor-pointer"
                        title="Download CSV"
                    >
                        <Download size={13} />
                        <span className="hidden sm:inline">Export</span>
                    </button>

                    <ColumnSelector columns={columns} onChange={handleColumnToggle} />
                </div>
            </div>

            {/* Virtual Scrollable Table Content with Pinned Live Row */}
            <div className="w-full relative overflow-hidden transition-all duration-300" style={{ height: density === 'compact' ? '580px' : '650px' }}>
                <TableVirtuoso
                    data={historyRows}
                    overscan={25}
                    style={{ height: '100%' }}
                    components={{
                        Table: React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>((props, ref) => (
                            <table ref={ref} {...props} className="w-full border-separate border-spacing-0" />
                        )),
                        TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>((props, ref) => (
                            <thead ref={ref} {...props} className="sticky top-0 z-30 shadow-sm bg-slate-50/95 dark:bg-slate-900/95 border-b border-slate-200 dark:border-slate-800 backdrop-blur-md" />
                        )),
                        TableRow: (props: any) => (
                            <tr {...props} className="hover:bg-indigo-500/[0.03] dark:hover:bg-indigo-500/[0.06] transition-colors border-b border-slate-100/60 dark:border-slate-800/60" />
                        ),
                    }}
                    fixedHeaderContent={() => (
                        <>
                            {/* Column Header Row */}
                            <tr className="bg-slate-50/95 dark:bg-slate-900/95 border-b border-slate-200 dark:border-slate-800 backdrop-blur-md">
                                {historyMode === 'intraday_history' && (
                                    <>
                                        {isVisible('time') && <HistoryTh align="left" side="neutral" compact={density === 'compact'}>Time</HistoryTh>}
                                        {isVisible('ce_total_oi') && <HistoryTh align="right" side="call" compact={density === 'compact'}>CE Total OI</HistoryTh>}
                                        {isVisible('pe_total_oi') && <HistoryTh align="right" side="put" compact={density === 'compact'}>PE Total OI</HistoryTh>}
                                        {isVisible('ce_total_change_oi') && <HistoryTh align="right" side="call" compact={density === 'compact'}>CE Total Chg OI</HistoryTh>}
                                        {isVisible('pe_total_change_oi') && <HistoryTh align="right" side="put" compact={density === 'compact'}>PE Total Chg OI</HistoryTh>}
                                        {isVisible('ce_total_volume') && <HistoryTh align="right" side="call" compact={density === 'compact'}>CE Total Vol</HistoryTh>}
                                        {isVisible('pe_total_volume') && <HistoryTh align="right" side="put" compact={density === 'compact'}>PE Total Vol</HistoryTh>}
                                        {isVisible('total_combined_oi') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Comb OI</HistoryTh>}
                                        {isVisible('total_combined_volume') && <HistoryTh align="right" side="put" compact={density === 'compact'}>Comb Vol</HistoryTh>}
                                        {isVisible('total_comb_oi_chg') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Comb OI Chg</HistoryTh>}
                                        {isVisible('total_comb_oi_chg_p') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Comb OI Chg%</HistoryTh>}
                                        {isVisible('total_vol_diff') && <HistoryTh align="center" side="net" compact={density === 'compact'}>Vol Diff & %</HistoryTh>}
                                        {isVisible('total_oi_diff') && <HistoryTh align="center" side="net" compact={density === 'compact'}>OI Diff & %</HistoryTh>}
                                        {isVisible('ce_total_vol_oi') && <HistoryTh align="right" side="call" compact={density === 'compact'}>CE Total Vol-OI</HistoryTh>}
                                        {isVisible('pe_total_vol_oi') && <HistoryTh align="right" side="put" compact={density === 'compact'}>PE Total Vol-OI</HistoryTh>}
                                        {isVisible('ce_total_vol_oi_chng') && <HistoryTh align="right" side="call" compact={density === 'compact'}>CE Total Vol-OI-Chng</HistoryTh>}
                                        {isVisible('pe_total_vol_oi_chng') && <HistoryTh align="right" side="put" compact={density === 'compact'}>PE Total Vol-OI-Chng</HistoryTh>}
                                        {isVisible('index_ltp') && <HistoryTh align="center" side="neutral" compact={density === 'compact'}>Index LTP</HistoryTh>}
                                    </>
                                )}

                                {historyMode === 'itm_otm_history' && (
                                    <>
                                        {isVisible('time') && <HistoryTh align="left" side="neutral" compact={density === 'compact'}>Time</HistoryTh>}
                                        {isVisible('v1strike') && <HistoryTh align="center" side="neutral" compact={density === 'compact'}>V1 Strike</HistoryTh>}
                                        {isVisible('itm_call') && <HistoryTh align="right" side="call" compact={density === 'compact'}>ITM Call</HistoryTh>}
                                        {isVisible('itm_put') && <HistoryTh align="right" side="put" compact={density === 'compact'}>ITM Put</HistoryTh>}
                                        {isVisible('itm_net') && <HistoryTh align="center" side="net" compact={density === 'compact'}>ITM Net & %</HistoryTh>}
                                        {isVisible('otm_call') && <HistoryTh align="right" side="call" compact={density === 'compact'}>OTM Call</HistoryTh>}
                                        {isVisible('otm_put') && <HistoryTh align="right" side="put" compact={density === 'compact'}>OTM Put</HistoryTh>}
                                        {isVisible('otm_net') && <HistoryTh align="center" side="net" compact={density === 'compact'}>OTM Net & %</HistoryTh>}
                                        {isVisible('final_net') && <HistoryTh align="center" side="net" compact={density === 'compact'}>Final Net & %</HistoryTh>}
                                        {isVisible('index_ltp') && <HistoryTh align="center" side="neutral" compact={density === 'compact'}>Index LTP</HistoryTh>}
                                    </>
                                )}

                                {historyMode === 'above_below_history' && (
                                    <>
                                        {isVisible('time') && <HistoryTh align="left" side="neutral" compact={density === 'compact'}>Time</HistoryTh>}
                                        {isVisible('atm_strike') && <HistoryTh align="center" side="neutral" compact={density === 'compact'}>ATM Strike</HistoryTh>}
                                        {isVisible('index_ltp') && <HistoryTh align="center" side="neutral" compact={density === 'compact'}>Index LTP</HistoryTh>}
                                        {isVisible('below_comb_oi') && <HistoryTh align="right" side="put" compact={density === 'compact'}>Below Comb OI</HistoryTh>}
                                        {isVisible('above_comb_oi') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Above Comb OI</HistoryTh>}
                                        {isVisible('net_comb_oi') && <HistoryTh align="center" side="net" compact={density === 'compact'}>Net OI & % (B - A)</HistoryTh>}
                                        {isVisible('below_comb_vol') && <HistoryTh align="right" side="put" compact={density === 'compact'}>Below Comb Vol</HistoryTh>}
                                        {isVisible('above_comb_vol') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Above Comb Vol</HistoryTh>}
                                        {isVisible('net_comb_vol') && <HistoryTh align="center" side="net" compact={density === 'compact'}>Net Vol & % (B - A)</HistoryTh>}
                                        {isVisible('below_comb_oi_chg') && <HistoryTh align="right" side="put" compact={density === 'compact'}>Below Comb OI Chg</HistoryTh>}
                                        {isVisible('above_comb_oi_chg') && <HistoryTh align="right" side="call" compact={density === 'compact'}>Above Comb OI Chg</HistoryTh>}
                                        {isVisible('net_comb_oi_chg') && <HistoryTh align="center" side="net" compact={density === 'compact'}>Net OI Chg & % (B - A)</HistoryTh>}
                                    </>
                                )}
                            </tr>

                            {/* Pinned Sticky LIVE Row (Always on Top) */}
                            {liveRow && (
                                <tr className="bg-emerald-50/90 dark:bg-emerald-950/70 border-b-2 border-emerald-500 shadow-sm backdrop-blur-md">
                                    {historyMode === 'intraday_history' && <IntradayHistoryRow row={liveRow} isVisible={isVisible} density={density} />}
                                    {historyMode === 'itm_otm_history' && <ItmOtmHistoryRow row={liveRow} isVisible={isVisible} density={density} />}
                                    {historyMode === 'above_below_history' && <AboveBelowHistoryRow row={liveRow} isVisible={isVisible} density={density} />}
                                </tr>
                            )}
                        </>
                    )}
                    itemContent={(index, row) => (
                        <>
                            {historyMode === 'intraday_history' && <IntradayHistoryRow row={row} isVisible={isVisible} density={density} />}
                            {historyMode === 'itm_otm_history' && <ItmOtmHistoryRow row={row} isVisible={isVisible} density={density} />}
                            {historyMode === 'above_below_history' && <AboveBelowHistoryRow row={row} isVisible={isVisible} density={density} />}
                        </>
                    )}
                />
            </div>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Custom Table Header Cell for Intraday History
// ----------------------------------------------------------------------------
interface HistoryThProps {
    children: React.ReactNode
    align?: 'left' | 'right' | 'center'
    side?: 'call' | 'put' | 'net' | 'neutral'
    className?: string
    compact?: boolean
}

const HistoryTh: React.FC<HistoryThProps> = memo(({ 
    children, 
    align = 'center', 
    side = 'neutral', 
    className = '',
    compact = true
}) => {
    let colorClass = 'text-slate-500 dark:text-slate-400 bg-slate-50/90 dark:bg-slate-900/90'
    if (side === 'call') {
        colorClass = 'text-rose-600 dark:text-rose-400 bg-rose-500/[0.04] dark:bg-rose-950/25'
    } else if (side === 'put') {
        colorClass = 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/[0.04] dark:bg-emerald-950/25'
    } else if (side === 'net') {
        colorClass = 'text-indigo-600 dark:text-indigo-400 bg-indigo-500/[0.06] dark:bg-indigo-950/35 font-extrabold'
    }

    const alignClass = align === 'left' 
        ? (compact ? 'text-left pl-3' : 'text-left pl-4') 
        : align === 'right' 
        ? (compact ? 'text-right pr-2' : 'text-right pr-3') 
        : 'text-center px-1.5'

    return (
        <th className={cn(
            "font-black uppercase tracking-wider whitespace-nowrap border-b border-slate-200 dark:border-slate-800 select-none transition-colors",
            compact ? "py-2 px-1.5 text-[10px] leading-tight" : "p-2.5 text-[11px] leading-normal",
            alignClass,
            colorClass,
            className
        )}>
            {children}
        </th>
    )
})

// ----------------------------------------------------------------------------
// Ultra-Premium Embedded Net Diff & Percentage Chip
// ----------------------------------------------------------------------------
interface NetDiffChipProps {
    value: number
    percent?: number
    formatter?: (v: number) => string
    density?: DensityMode
    className?: string
}

const NetDiffChip: React.FC<NetDiffChipProps> = memo(({
    value,
    percent,
    formatter,
    density = 'compact',
    className = ''
}) => {
    const isBullish = value > 0
    const isBearish = value < 0
    const isNeutral = value === 0 || isNaN(value)

    const formattedVal = formatter 
        ? formatter(value) 
        : (isNeutral ? '0' : formatIndianScale(value))

    // Ensure sign prefix for non-zero values if formatter doesn't supply it
    const displayVal = isBullish && !formattedVal.startsWith('+') ? `+${formattedVal}` : formattedVal

    const pctVal = percent !== undefined && !isNaN(percent) ? percent : 0
    const pctSign = pctVal > 0 ? '+' : ''
    const pctDisplay = `${pctSign}${pctVal.toFixed(1)}%`

    return (
        <div className={cn(
            "inline-flex items-center justify-between rounded-lg border font-mono tabular-nums shadow-2xs select-none transition-all",
            isBullish 
                ? "bg-emerald-50/80 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200/70 dark:border-emerald-800/50"
                : isBearish 
                ? "bg-rose-50/80 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border-rose-200/70 dark:border-rose-800/50"
                : "bg-slate-100/70 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border-slate-200/70 dark:border-slate-700/60",
            density === 'compact' 
                ? "h-[25px] px-1.5 py-0.5 gap-1.5 text-[11px]" 
                : "h-[30px] px-2 py-1 gap-2 text-xs",
            className
        )}>
            {/* Trend Indicator & Value */}
            <span className="inline-flex items-center gap-1 font-black">
                {isBullish ? (
                    <TrendingUp size={density === 'compact' ? 11 : 12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                ) : isBearish ? (
                    <TrendingDown size={density === 'compact' ? 11 : 12} className="text-rose-600 dark:text-rose-400 shrink-0" />
                ) : (
                    <Minus size={density === 'compact' ? 11 : 12} className="text-slate-400 shrink-0" />
                )}
                <span className="tracking-tight">{displayVal}</span>
            </span>

            {/* Embedded Diff % Badge */}
            <span className={cn(
                "font-black tracking-tight rounded shrink-0",
                density === 'compact' 
                    ? "px-1 py-[0.5px] text-[9.5px]" 
                    : "px-1.5 py-0.5 text-[10.5px]",
                isBullish
                    ? "bg-emerald-500/20 text-emerald-800 dark:text-emerald-200"
                    : isBearish
                    ? "bg-rose-500/20 text-rose-800 dark:text-rose-200"
                    : "bg-slate-200/70 dark:bg-slate-700/70 text-slate-600 dark:text-slate-400"
            )}>
                {pctDisplay}
            </span>
        </div>
    )
})

// ----------------------------------------------------------------------------
// Base Table Cell Class Helper for Dynamic Density
// ----------------------------------------------------------------------------
const getTdBase = (density: DensityMode = 'compact', extra?: string) => cn(
    "align-middle whitespace-nowrap border-b border-slate-100/70 dark:border-slate-800/60 font-semibold transition-colors",
    density === 'compact' ? "h-[36px] py-0.5 px-2 text-[11px]" : "h-[46px] py-1.5 px-2.5 text-xs",
    extra
)

// ----------------------------------------------------------------------------
// Memoized Row Component for Virtual Scroll Performance: Intraday History
// ----------------------------------------------------------------------------
interface IntradayHistoryRowProps {
    row: any
    isVisible: (key: string) => boolean
    density?: DensityMode
}

const IntradayHistoryRow = memo<IntradayHistoryRowProps>(({ row, isVisible, density = 'compact' }) => {
    return (
        <>
            {/* Time */}
            {isVisible('time') && (
                <td className={getTdBase(density, `text-left font-black text-slate-700 dark:text-slate-200 tabular-nums ${density === 'compact' ? 'pl-3' : 'pl-4'}`)}>
                    {row.isLive ? (
                        <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/20 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-mono font-black border border-emerald-500/40 shadow-xs ring-1 ring-emerald-500/30",
                            density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
                        )}>
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                            </span>
                            LIVE ({row.timeStr})
                        </span>
                    ) : row.isPD || row.timeStr?.startsWith('PD') ? (
                        <span className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-500/15 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-mono shadow-xs border border-amber-500/30",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            <span className="px-1 rounded text-[9px] bg-amber-500/25 font-black text-amber-800 dark:text-amber-200">PD</span>
                            {row.timeStr.replace(/^PD\s*/, '')}
                        </span>
                    ) : (
                        <span className={cn(
                            "inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono shadow-xs",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            {row.timeStr}
                        </span>
                    )}
                </td>
            )}

            {/* CE Total OI */}
            {isVisible('ce_total_oi') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <DataChip value={formatOI(row.ceTotalOI)} color="red" className="ml-auto font-bold" />
                </td>
            )}

            {/* PE Total OI */}
            {isVisible('pe_total_oi') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-emerald-500/5 dark:bg-emerald-950/20' : 'bg-emerald-50/10 dark:bg-emerald-950/5'}`)}>
                    <DataChip value={formatOI(row.peTotalOI)} color="green" className="ml-auto font-bold" />
                </td>
            )}

            {/* CE Total Change OI */}
            {isVisible('ce_total_change_oi') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <DataChip 
                        value={formatOI(row.ceTotalChangeOI)} 
                        color={row.ceTotalChangeOI >= 0 ? 'green' : 'red'} 
                        className="ml-auto" 
                    />
                </td>
            )}

            {/* PE Total Change OI */}
            {isVisible('pe_total_change_oi') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-emerald-500/5 dark:bg-emerald-950/20' : 'bg-emerald-50/10 dark:bg-emerald-950/5'}`)}>
                    <DataChip 
                        value={formatOI(row.peTotalChangeOI)} 
                        color={row.peTotalChangeOI >= 0 ? 'green' : 'red'} 
                        className="ml-auto" 
                    />
                </td>
            )}

            {/* CE Total Volume */}
            {isVisible('ce_total_volume') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <DataChip value={formatVolume(row.ceTotalVolume)} color="red" className="ml-auto" />
                </td>
            )}

            {/* PE Total Volume */}
            {isVisible('pe_total_volume') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-emerald-500/5 dark:bg-emerald-950/20' : 'bg-emerald-50/10 dark:bg-emerald-950/5'}`)}>
                    <DataChip value={formatVolume(row.peTotalVolume)} color="green" className="ml-auto" />
                </td>
            )}

            {/* Total Combined OI */}
            {isVisible('total_combined_oi') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <DataChip value={formatOI(row.totalCombinedOI)} color="red" className="ml-auto font-black" />
                </td>
            )}

            {/* Total Combined Volume */}
            {isVisible('total_combined_volume') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-emerald-500/5 dark:bg-emerald-950/20' : 'bg-emerald-50/10 dark:bg-emerald-950/5'}`)}>
                    <DataChip value={formatVolume(row.totalCombinedVolume)} color="green" className="ml-auto font-black" />
                </td>
            )}

            {/* Total Comb OI Chg */}
            {isVisible('total_comb_oi_chg') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <DataChip 
                        value={formatOI(row.totalCombOIChg)} 
                        color={row.totalCombOIChg >= 0 ? 'green' : 'red'} 
                        className="ml-auto font-bold" 
                    />
                </td>
            )}

            {/* Total Comb OI Chg% */}
            {isVisible('total_comb_oi_chg_p') && (
                <td className={getTdBase(density, `text-right ${row.isLive ? 'bg-rose-500/5 dark:bg-rose-950/20' : 'bg-rose-50/10 dark:bg-rose-950/5'}`)}>
                    <span className={cn(
                        "inline-flex items-center font-black tabular-nums shadow-xs rounded-md",
                        density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-[11px]",
                        row.totalCombOIChgP >= 0 
                            ? "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/50" 
                            : "bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 border border-rose-200/50 dark:border-rose-800/50"
                    )}>
                        {row.totalCombOIChgP >= 0 ? '+' : ''}{row.totalCombOIChgP.toFixed(1)}%
                    </span>
                </td>
            )}

            {/* Total Vol Diff & % (Embedded) */}
            {isVisible('total_vol_diff') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.totalVolDiff} 
                        percent={row.totalVolDiffP}
                        formatter={formatVolume}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* Total OI Diff & % (Embedded) */}
            {isVisible('total_oi_diff') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.totalOIDiff} 
                        percent={row.totalOIDiffP}
                        formatter={formatOI}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* CE Total Vol-OI */}
            {isVisible('ce_total_vol_oi') && (
                <td className={getTdBase(density, 'text-right')}>
                    <DataChip 
                        value={formatVolume(row.ceTotalVolOI)} 
                        color={row.ceTotalVolOI >= 0 ? 'green' : 'red'} 
                        className="ml-auto font-bold" 
                    />
                </td>
            )}

            {/* PE Total Vol-OI */}
            {isVisible('pe_total_vol_oi') && (
                <td className={getTdBase(density, 'text-right')}>
                    <DataChip 
                        value={formatVolume(row.peTotalVolOI)} 
                        color={row.peTotalVolOI >= 0 ? 'green' : 'red'} 
                        className="ml-auto font-bold" 
                    />
                </td>
            )}

            {/* CE Total Vol-OI-Chng */}
            {isVisible('ce_total_vol_oi_chng') && (
                <td className={getTdBase(density, 'text-right')}>
                    <DataChip 
                        value={formatVolume(row.ceTotalVolOIChng)} 
                        color={row.ceTotalVolOIChng >= 0 ? 'green' : 'red'} 
                        className="ml-auto font-black" 
                    />
                </td>
            )}

            {/* PE Total Vol-OI-Chng */}
            {isVisible('pe_total_vol_oi_chng') && (
                <td className={getTdBase(density, 'text-right')}>
                    <DataChip 
                        value={formatVolume(row.peTotalVolOIChng)} 
                        color={row.peTotalVolOIChng >= 0 ? 'green' : 'red'} 
                        className="ml-auto font-black" 
                    />
                </td>
            )}

            {/* Index LTP */}
            {isVisible('index_ltp') && (
                <td className={getTdBase(density, 'text-center')}>
                    <span className={cn(
                        "inline-flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-black tabular-nums shadow-inner ring-1 ring-slate-200/50 dark:ring-slate-700/50",
                        density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
                    )}>
                        {row.indexLtp > 0 ? row.indexLtp.toFixed(2) : '-'}
                    </span>
                </td>
            )}
        </>
    )
})

// ----------------------------------------------------------------------------
// Memoized Row Component for Virtual Scroll Performance: ITM / OTM History
// ----------------------------------------------------------------------------
interface ItmOtmHistoryRowProps {
    row: any
    isVisible: (key: string) => boolean
    density?: DensityMode
}

const ItmOtmHistoryRow = memo<ItmOtmHistoryRowProps>(({ row, isVisible, density = 'compact' }) => {
    return (
        <>
            {/* Time */}
            {isVisible('time') && (
                <td className={getTdBase(density, `text-left font-black text-slate-700 dark:text-slate-200 tabular-nums ${density === 'compact' ? 'pl-3' : 'pl-4'}`)}>
                    {row.isLive ? (
                        <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/20 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-mono font-black border border-emerald-500/40 shadow-xs ring-1 ring-emerald-500/30",
                            density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
                        )}>
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                            </span>
                            LIVE ({row.timeStr})
                        </span>
                    ) : row.isPD || row.timeStr?.startsWith('PD') ? (
                        <span className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-500/15 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-mono font-bold shadow-xs border border-amber-500/30",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            <span className="px-1 rounded text-[9px] bg-amber-500/25 font-black text-amber-800 dark:text-amber-200">PD</span>
                            {row.timeStr.replace(/^PD\s*/, '')}
                        </span>
                    ) : (
                        <span className={cn(
                            "inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono font-bold shadow-xs",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            {row.timeStr}
                        </span>
                    )}
                </td>
            )}

            {/* V1 Strike */}
            {isVisible('v1strike') && (
                <td className={getTdBase(density, 'text-center')}>
                    <StrikeChip strike={row.atmStrike} type="atm" />
                </td>
            )}

            {/* ITM Call */}
            {isVisible('itm_call') && (
                <td className={getTdBase(density, `text-right text-rose-600 dark:text-rose-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.itmCall)}
                </td>
            )}

            {/* ITM Put */}
            {isVisible('itm_put') && (
                <td className={getTdBase(density, `text-right text-emerald-600 dark:text-emerald-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.itmPut)}
                </td>
            )}

            {/* ITM Net & % (Embedded) */}
            {isVisible('itm_net') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.itmNet} 
                        percent={row.itmNetP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* OTM Call */}
            {isVisible('otm_call') && (
                <td className={getTdBase(density, `text-right text-rose-600 dark:text-rose-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.otmCall)}
                </td>
            )}

            {/* OTM Put */}
            {isVisible('otm_put') && (
                <td className={getTdBase(density, `text-right text-emerald-600 dark:text-emerald-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.otmPut)}
                </td>
            )}

            {/* OTM Net & % (Embedded) */}
            {isVisible('otm_net') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.otmNet} 
                        percent={row.otmNetP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* Final Net & % (Embedded) */}
            {isVisible('final_net') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.finalNet} 
                        percent={row.finalNetP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* Index LTP */}
            {isVisible('index_ltp') && (
                <td className={getTdBase(density, 'text-center')}>
                    <span className={cn(
                        "inline-flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-black tabular-nums shadow-inner ring-1 ring-slate-200/50 dark:ring-slate-700/50",
                        density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
                    )}>
                        {row.indexLtp > 0 ? row.indexLtp.toFixed(2) : '-'}
                    </span>
                </td>
            )}
        </>
    )
})

// ----------------------------------------------------------------------------
// Memoized Row Component for Virtual Scroll Performance: Above / Below History
// ----------------------------------------------------------------------------
interface AboveBelowHistoryRowProps {
    row: any
    isVisible: (key: string) => boolean
    density?: DensityMode
}

const AboveBelowHistoryRow = memo<AboveBelowHistoryRowProps>(({ row, isVisible, density = 'compact' }) => {
    return (
        <>
            {/* Time */}
            {isVisible('time') && (
                <td className={getTdBase(density, `text-left font-black text-slate-700 dark:text-slate-200 tabular-nums ${density === 'compact' ? 'pl-3' : 'pl-4'}`)}>
                    {row.isLive ? (
                        <span className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/20 dark:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 font-mono font-black border border-emerald-500/40 shadow-xs ring-1 ring-emerald-500/30",
                            density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
                        )}>
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                            </span>
                            LIVE ({row.timeStr})
                        </span>
                    ) : row.isPD || row.timeStr?.startsWith('PD') ? (
                        <span className={cn(
                            "inline-flex items-center gap-1 rounded-md bg-amber-500/15 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-mono font-bold shadow-xs border border-amber-500/30",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            <span className="px-1 rounded text-[9px] bg-amber-500/25 font-black text-amber-800 dark:text-amber-200">PD</span>
                            {row.timeStr.replace(/^PD\s*/, '')}
                        </span>
                    ) : (
                        <span className={cn(
                            "inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-mono font-bold shadow-xs",
                            density === 'compact' ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-[11px]"
                        )}>
                            {row.timeStr}
                        </span>
                    )}
                </td>
            )}

            {/* ATM Strike */}
            {isVisible('atm_strike') && (
                <td className={getTdBase(density, 'text-center')}>
                    <StrikeChip strike={row.atmStrike} type="atm" />
                </td>
            )}

            {/* Index LTP */}
            {isVisible('index_ltp') && (
                <td className={getTdBase(density, 'text-center')}>
                    <span className={cn(
                        "inline-flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-black tabular-nums shadow-inner ring-1 ring-slate-200/50 dark:ring-slate-700/50",
                        density === 'compact' ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"
                    )}>
                        {row.indexLtp > 0 ? row.indexLtp.toFixed(2) : '-'}
                    </span>
                </td>
            )}

            {/* Below Comb OI */}
            {isVisible('below_comb_oi') && (
                <td className={getTdBase(density, `text-right text-emerald-600 dark:text-emerald-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.belowCombOI)}
                </td>
            )}

            {/* Above Comb OI */}
            {isVisible('above_comb_oi') && (
                <td className={getTdBase(density, `text-right text-rose-600 dark:text-rose-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.aboveCombOI)}
                </td>
            )}

            {/* Net OI & % (B - A) (Embedded) */}
            {isVisible('net_comb_oi') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.netCombOI} 
                        percent={row.netCombOIP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* Below Comb Vol */}
            {isVisible('below_comb_vol') && (
                <td className={getTdBase(density, `text-right text-emerald-600 dark:text-emerald-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.belowCombVol)}
                </td>
            )}

            {/* Above Comb Vol */}
            {isVisible('above_comb_vol') && (
                <td className={getTdBase(density, `text-right text-rose-600 dark:text-rose-400 tabular-nums font-bold ${density === 'compact' ? 'pr-2' : 'pr-3'}`)}>
                    {formatIndianScale(row.aboveCombVol)}
                </td>
            )}

            {/* Net Vol & % (B - A) (Embedded) */}
            {isVisible('net_comb_vol') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.netCombVol} 
                        percent={row.netCombVolP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}

            {/* Below Comb OI Chg */}
            {isVisible('below_comb_oi_chg') && (
                <td className={getTdBase(density, `text-right tabular-nums ${density === 'compact' ? 'pr-2' : 'pr-3'} ${row.belowCombOIChg >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`)}>
                    {(row.belowCombOIChg > 0 ? '+' : '') + (row.belowCombOIChg === 0 ? '0' : formatIndianScale(row.belowCombOIChg))}
                </td>
            )}

            {/* Above Comb OI Chg */}
            {isVisible('above_comb_oi_chg') && (
                <td className={getTdBase(density, `text-right tabular-nums ${density === 'compact' ? 'pr-2' : 'pr-3'} ${row.aboveCombOIChg >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`)}>
                    {(row.aboveCombOIChg > 0 ? '+' : '') + (row.aboveCombOIChg === 0 ? '0' : formatIndianScale(row.aboveCombOIChg))}
                </td>
            )}

            {/* Net OI Chg & % (B - A) (Embedded) */}
            {isVisible('net_comb_oi_chg') && (
                <td className={getTdBase(density, 'text-center')}>
                    <NetDiffChip 
                        value={row.netCombOIChg} 
                        percent={row.netCombOIChgP}
                        formatter={formatIndianScale}
                        density={density}
                        className="mx-auto"
                    />
                </td>
            )}
        </>
    )
})

// ----------------------------------------------------------------------------
// Helper: Extract Options List from ANY snapshot format
// ----------------------------------------------------------------------------
function extractOptionsFromSnap(snap: any, symbol: string, expiry?: string): any[] {
    if (!snap) return []

    // 1. Direct options arrays
    if (Array.isArray(snap.options) && snap.options.length > 0) return snap.options
    if (Array.isArray(snap.rawOpts) && snap.rawOpts.length > 0) return snap.rawOpts
    if (Array.isArray(snap.opts) && snap.opts.length > 0) return snap.opts
    if (Array.isArray(snap.filteredOptions) && snap.filteredOptions.length > 0) return snap.filteredOptions
    if (Array.isArray(snap.optionChain) && snap.optionChain.length > 0) return snap.optionChain
    if (Array.isArray(snap.chain) && snap.chain.length > 0) return snap.chain
    if (Array.isArray(snap.records) && snap.records.length > 0) return snap.records
    if (Array.isArray(snap.data) && snap.data.length > 0) return snap.data

    // 2. optionsData format (nested Record<symbol, Record<expiry, OptionData[]>>)
    if (snap.optionsData && typeof snap.optionsData === 'object') {
        const cleanSym = (symbol || '').replace(/[^A-Z0-9]/ig, '').toUpperCase()
        const symKey = Object.keys(snap.optionsData).find(k => {
            const cleanK = k.replace(/[^A-Z0-9]/ig, '').toUpperCase()
            return cleanK === cleanSym || cleanK.includes(cleanSym) || cleanSym.includes(cleanK)
        })
        const symObj = symKey ? snap.optionsData[symKey] : Object.values(snap.optionsData)[0]
        if (symObj && typeof symObj === 'object') {
            if (expiry) {
                const cleanExp = expiry.replace(/[^0-9]/g, '')
                for (const [expKey, optList] of Object.entries(symObj)) {
                    const cleanKey = expKey.replace(/[^0-9]/g, '')
                    if (expKey === expiry || (cleanKey && cleanKey === cleanExp)) {
                        if (Array.isArray(optList) && optList.length > 0) {
                            return optList
                        }
                    }
                }
            }
            // Return first non-empty expiry array
            for (const expKey of Object.keys(symObj)) {
                if (Array.isArray(symObj[expKey]) && symObj[expKey].length > 0) {
                    return symObj[expKey]
                }
            }
        }
    }

    // 3. Upstox strategyChainData.strikeMap
    const rawChain = snap?.strategyChainData || snap?.data?.strategyChainData
    const strikeMap = rawChain?.strikeMap
    if (strikeMap && typeof strikeMap === 'object') {
        const mapped: any[] = []
        for (const [k, v] of Object.entries(strikeMap as Record<string, any>)) {
            const strike = parseFloat(k)
            if (isNaN(strike)) continue
            const ce = v?.callOptionData || {}
            const pe = v?.putOptionData || {}
            mapped.push({
                strike,
                strikePrice: strike,
                callOpenInterest: ce.marketData?.oi ?? ce.oi ?? 0,
                putOpenInterest: pe.marketData?.oi ?? pe.oi ?? 0,
                callOpenInterestChange: (ce.marketData?.oi ?? 0) - (ce.marketData?.prevOi ?? ce.prevOi ?? ce.marketData?.oi ?? 0),
                putOpenInterestChange: (pe.marketData?.oi ?? 0) - (pe.marketData?.prevOi ?? pe.prevOi ?? pe.marketData?.oi ?? 0),
                callVolume: ce.marketData?.volume ?? ce.volume ?? 0,
                putVolume: pe.marketData?.volume ?? pe.volume ?? 0,
                callPrice: ce.marketData?.ltp ?? ce.ltp ?? 0,
                putPrice: pe.marketData?.ltp ?? pe.ltp ?? 0,
            })
        }
        if (mapped.length > 0) return mapped.sort((a, b) => a.strike - b.strike)
    }

    return []
}

// ----------------------------------------------------------------------------
// Helper: Extract Spot / Index Price from ANY snapshot format
// ----------------------------------------------------------------------------
function extractSpotPriceFromSnap(snap: any, options: any[], symbol: string): number {
    if (!snap) return 0
    let spot = Number(snap.spotPrice || snap.indexLtp || snap.index_ltp || snap.underlyingPrice || snap.data?.ltp || 0)
    if (spot > 0) return spot

    // Try snap.indexData
    if (snap.indexData && typeof snap.indexData === 'object') {
        const symKey = Object.keys(snap.indexData).find(k => 
            k.toUpperCase().includes(symbol.toUpperCase()) || 
            symbol.toUpperCase().includes(k.toUpperCase())
        )
        const idx = symKey ? snap.indexData[symKey] : Object.values(snap.indexData)[0]
        spot = Number(idx?.lastPrice || idx?.ltp || idx?.spotPrice || 0)
        if (spot > 0) return spot
    }

    // Try optionChainExtendedData
    spot = Number(snap.optionChainExtendedData?.assetLtpc?.ltp || snap?.data?.optionChainExtendedData?.assetLtpc?.ltp || 0)
    if (spot > 0) return spot

    // Fallback: estimate from median strike
    if (options.length > 0) {
        const midIdx = Math.floor(options.length / 2)
        return Number(options[midIdx]?.strike || options[midIdx]?.strikePrice || 0)
    }

    return 0
}

// ----------------------------------------------------------------------------
// Helper: Robust Time String Extractor
// ----------------------------------------------------------------------------
function extractTimeString(rawTime: any): string {
    if (!rawTime) return formatISTTime(Date.now())
    if (rawTime instanceof Date) return formatISTTime(rawTime)
    
    // If it's a number (timestamp in ms)
    if (typeof rawTime === 'number') {
        return formatISTTime(rawTime)
    }
    
    const str = String(rawTime).trim()
    if (!str) return formatISTTime(Date.now())
    
    // If numeric string timestamp (e.g. "1724050000000")
    if (/^\d{10,13}$/.test(str)) {
        return formatISTTime(Number(str))
    }
    
    // ISO format: "2026-08-19T12:45:00.000Z"
    if (str.includes('T')) {
        const timePart = str.split('T')[1] || ''
        return timePart.substring(0, 8).includes(':') ? timePart.substring(0, 5) : formatISTTime(Date.now())
    }
    
    // Date & Time with space: "19-08-2026 12:45:00" or "2026-08-19 12:45:00"
    if (str.includes(' ')) {
        const parts = str.split(' ')
        const timePart = parts.find(p => p.includes(':'))
        if (timePart) {
            return timePart.substring(0, 5)
        }
    }
    
    // Pure time string: "12:45" or "12:45:30"
    if (str.includes(':')) {
        return str.substring(0, 5)
    }
    
    // Fallback: try parsing Date
    const parsed = new Date(str)
    if (!isNaN(parsed.getTime())) {
        return formatISTTime(parsed)
    }
    
    return str
}

// ----------------------------------------------------------------------------
// Helper: Deduplicate Snapshots by Minute (Keeps ONLY the latest backup per minute)
// ----------------------------------------------------------------------------
function deduplicateSnapshotsByMinute(rawSnapshots: any[]): any[] {
    if (!Array.isArray(rawSnapshots) || rawSnapshots.length === 0) return []

    // Map of minuteKey -> { snap, timestampMs, index }
    const minuteMap = new Map<string, { snap: any; timestampMs: number; index: number }>()

    for (let i = 0; i < rawSnapshots.length; i++) {
        const snap = rawSnapshots[i]
        if (!snap) continue

        const rawT = snap.timestamp || snap.timestampIST || snap.time || snap.TimeStamp || snap.datetime || snap.timeStr || 0
        let timestampMs = 0

        if (typeof rawT === 'number') {
            timestampMs = rawT < 1e11 ? rawT * 1000 : rawT
        } else if (rawT instanceof Date) {
            timestampMs = rawT.getTime()
        } else if (typeof rawT === 'string') {
            const num = Number(rawT)
            if (!isNaN(num) && num > 0) {
                timestampMs = num < 1e11 ? num * 1000 : num
            } else {
                const d = new Date(rawT)
                if (!isNaN(d.getTime())) {
                    timestampMs = d.getTime()
                }
            }
        }

        const timeStr = extractTimeString(rawT)
        if (!timeStr || timeStr === '--:--') continue

        const isPD = !!snap.isPD || (typeof snap.time === 'string' && snap.time.startsWith('PD')) || snap.dayType === 'PD'
        const prefix = isPD ? 'PD_' : 'CUR_'

        // Extract date key if available to avoid colliding same minute on different days
        let dateStr = snap.date || snap.dateStr || snap.Date || ''
        if (!dateStr && timestampMs > 0) {
            const d = new Date(timestampMs)
            dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        }

        const minuteKey = dateStr ? `${prefix}${dateStr}_${timeStr}` : `${prefix}${timeStr}`

        const existing = minuteMap.get(minuteKey)
        if (!existing) {
            minuteMap.set(minuteKey, { snap, timestampMs, index: i })
        } else {
            // Consider ONLY the last latest backup for one minute:
            // 1. If timestampMs is available and strictly greater -> replace
            // 2. If timestamps are equal or zero, the later index in array is the latest backup -> replace
            if (timestampMs > existing.timestampMs) {
                minuteMap.set(minuteKey, { snap, timestampMs, index: i })
            } else if (timestampMs === existing.timestampMs && i >= existing.index) {
                minuteMap.set(minuteKey, { snap, timestampMs, index: i })
            }
        }
    }

    // Sort deduplicated snapshots chronologically (ascending: earliest to latest)
    // PD day should strictly precede CURRENT day
    return Array.from(minuteMap.values())
        .sort((a, b) => {
            const aIsPD = !!a.snap.isPD || (typeof a.snap.time === 'string' && a.snap.time.startsWith('PD')) || a.snap.dayType === 'PD'
            const bIsPD = !!b.snap.isPD || (typeof b.snap.time === 'string' && b.snap.time.startsWith('PD')) || b.snap.dayType === 'PD'
            if (aIsPD !== bIsPD) {
                return aIsPD ? -1 : 1
            }
            if (a.timestampMs && b.timestampMs && a.timestampMs !== b.timestampMs) {
                return a.timestampMs - b.timestampMs
            }
            return a.index - b.index
        })
        .map((item) => item.snap)
}

// ----------------------------------------------------------------------------
// Helper: Compute Aggregated Row Metrics for a Snapshot
// ----------------------------------------------------------------------------
function computeSnapshotMetrics(
    snap: any, 
    symbol: string, 
    expiry: string | undefined,
    strikeRange: number, 
    subTab: IntradaySubTab, 
    includeAtm: boolean
) {
    const rawTime = snap?.timestamp || snap?.timestampIST || snap?.time || snap?.TimeStamp || snap?.timestamp_ist || Date.now()
    const isPD = !!snap?.isPD || (typeof snap?.time === 'string' && snap.time.startsWith('PD')) || snap?.dayType === 'PD'
    const dayType = snap?.dayType || (isPD ? 'PD' : 'CURRENT')
    let timeStr = extractTimeString(rawTime)
    if (isPD && !timeStr.startsWith('PD')) {
        timeStr = 'PD ' + timeStr
    }

    const options = extractOptionsFromSnap(snap, symbol, expiry)
    const spotPrice = extractSpotPriceFromSnap(snap, options, symbol)
    const step = getStrikeStep(symbol, options)
    const atmStrike = spotPrice > 0 ? Math.round(spotPrice / step) * step : 0

    const minStrike = atmStrike > 0 ? atmStrike - (strikeRange * step) : 0
    const maxStrike = atmStrike > 0 ? atmStrike + (strikeRange * step) : Infinity

    let ceTotalOI = 0
    let peTotalOI = 0
    let ceTotalChangeOI = 0
    let peTotalChangeOI = 0
    let ceTotalVolume = 0
    let peTotalVolume = 0

    let ceTotalVolOI = 0
    let peTotalVolOI = 0
    let ceTotalVolOIChng = 0
    let peTotalVolOIChng = 0

    for (const opt of options) {
        const strike = Number(opt.strike || opt.strikePrice || opt.Strike || opt.STRIKE || 0)
        if (atmStrike > 0 && (strike < minStrike || strike > maxStrike)) continue

        const isAtm = strike === atmStrike
        if (!includeAtm && isAtm && atmStrike > 0) continue

        const ceOI = Number(opt.callOpenInterest ?? opt.ceOI ?? opt.ce_oi ?? opt.CE_OI ?? opt['CE OI'] ?? opt.callOi ?? 0)
        const peOI = Number(opt.putOpenInterest ?? opt.peOI ?? opt.pe_oi ?? opt.PE_OI ?? opt['PE OI'] ?? opt.putOi ?? 0)
        
        const ceOIChg = Number(opt.callOpenInterestChange ?? opt.ceOiChange ?? opt.ce_oi_change ?? opt.CE_oi_chg ?? opt.call_oi_chg ?? opt['CE Change in OI'] ?? opt['CE OI Chg'] ?? opt.ceChangeOI ?? 0)
        const peOIChg = Number(opt.putOpenInterestChange ?? opt.peOiChange ?? opt.pe_oi_change ?? opt.PE_oi_chg ?? opt.put_oi_chg ?? opt['PE Change in OI'] ?? opt['PE OI Chg'] ?? opt.peChangeOI ?? 0)
        
        const ceVol = Number(opt.callVolume ?? opt.ceVol ?? opt.ce_vol ?? opt.CE_Vol ?? opt['CE Vol'] ?? opt['CE Volume'] ?? opt.callVol ?? 0)
        const peVol = Number(opt.putVolume ?? opt.peVol ?? opt.pe_vol ?? opt.PE_Vol ?? opt['PE Vol'] ?? opt['PE Volume'] ?? opt.putVol ?? 0)

        // Calculate Vol-OI and Vol-OI-Chng per strike
        const ceVolOi = ceVol - ceOI
        const ceVolOiChng = ceOIChg > 0 ? (ceVolOi - ceOIChg) : (ceVolOi + ceOIChg)

        const peVolOi = peVol - peOI
        const peVolOiChng = peOIChg > 0 ? (peVolOi - peOIChg) : (peVolOi + peOIChg)

        // SubTab Filter
        if (subTab === 'All' || atmStrike === 0) {
            ceTotalOI += ceOI
            peTotalOI += peOI
            ceTotalChangeOI += ceOIChg
            peTotalChangeOI += peOIChg
            ceTotalVolume += ceVol
            peTotalVolume += peVol
            ceTotalVolOI += ceVolOi
            peTotalVolOI += peVolOi
            ceTotalVolOIChng += ceVolOiChng
            peTotalVolOIChng += peVolOiChng
        } else if (subTab === 'ITM') {
            // ITM: CE strike < atmStrike, PE strike > atmStrike
            if (strike < atmStrike) {
                ceTotalOI += ceOI
                ceTotalChangeOI += ceOIChg
                ceTotalVolume += ceVol
                ceTotalVolOI += ceVolOi
                ceTotalVolOIChng += ceVolOiChng
            }
            if (strike > atmStrike) {
                peTotalOI += peOI
                peTotalChangeOI += peOIChg
                peTotalVolume += peVol
                peTotalVolOI += peVolOi
                peTotalVolOIChng += peVolOiChng
            }
        } else if (subTab === 'OTM') {
            // OTM: CE strike > atmStrike, PE strike < atmStrike
            if (strike > atmStrike) {
                ceTotalOI += ceOI
                ceTotalChangeOI += ceOIChg
                ceTotalVolume += ceVol
                ceTotalVolOI += ceVolOi
                ceTotalVolOIChng += ceVolOiChng
            }
            if (strike < atmStrike) {
                peTotalOI += peOI
                peTotalChangeOI += peOIChg
                peTotalVolume += peVol
                peTotalVolOI += peVolOi
                peTotalVolOIChng += peVolOiChng
            }
        }
    }

    const totalCombinedOI = ceTotalOI + peTotalOI
    const totalCombinedVolume = ceTotalVolume + peTotalVolume
    const totalCombOIChg = ceTotalChangeOI + peTotalChangeOI
    const totalCombOIChgP = (totalCombinedOI - totalCombOIChg) > 0 
        ? (totalCombOIChg / (totalCombinedOI - totalCombOIChg)) * 100 
        : 0

    const totalVolDiff = ceTotalVolume - peTotalVolume
    const totalVolDiffP = totalCombinedVolume > 0 ? (totalVolDiff / totalCombinedVolume) * 100 : 0
    const totalOIDiff = peTotalOI - ceTotalOI
    const totalOIDiffP = totalCombinedOI > 0 ? (totalOIDiff / totalCombinedOI) * 100 : 0

    return {
        timeStr,
        timestamp: rawTime,
        isLive: false,
        isPD,
        dayType,
        ceTotalOI,
        peTotalOI,
        ceTotalChangeOI,
        peTotalChangeOI,
        ceTotalVolume,
        peTotalVolume,
        totalCombinedOI,
        totalCombinedVolume,
        totalCombOIChg,
        totalCombOIChgP,
        totalVolDiff,
        totalVolDiffP,
        totalOIDiff,
        totalOIDiffP,
        ceTotalVolOI,
        peTotalVolOI,
        ceTotalVolOIChng,
        peTotalVolOIChng,
        indexLtp: spotPrice
    }
}

// ----------------------------------------------------------------------------
// Compute ITM / OTM Snapshot Metrics
// ----------------------------------------------------------------------------
function computeItmOtmSnapshot(
    snap: any,
    symbol: string,
    expiry: string | undefined,
    strikeRange: number,
    metric: 'Volume' | 'OI' | 'Chng in OI',
    includeAtm: boolean
): any {
    const rawTime = snap.timestamp || snap.timestampIST || snap.time || snap.TimeStamp || snap.datetime || snap.timeStr || Date.now()
    const isPD = !!snap?.isPD || (typeof snap?.time === 'string' && snap.time.startsWith('PD')) || snap?.dayType === 'PD'
    const dayType = snap?.dayType || (isPD ? 'PD' : 'CURRENT')
    let timeStr = extractTimeString(rawTime)
    if (isPD && !timeStr.startsWith('PD')) {
        timeStr = 'PD ' + timeStr
    }
    const options = extractOptionsFromSnap(snap, symbol, expiry)
    const spotPrice = extractSpotPriceFromSnap(snap, options, symbol)
    const step = getStrikeStep(symbol, options)

    let atmStrike = Number(snap.atmStrike || 0)
    if (atmStrike <= 0 && spotPrice > 0) {
        atmStrike = Math.round(spotPrice / step) * step
    }
    if (atmStrike <= 0 && options.length > 0) {
        atmStrike = Number(options[Math.floor(options.length / 2)]?.strike || options[0]?.strike || 0)
    }

    const activeRange = strikeRange || 10
    const minStrike = atmStrike > 0 ? atmStrike - activeRange * step : 0
    const maxStrike = atmStrike > 0 ? atmStrike + activeRange * step : Infinity

    let itmCall = 0
    let itmPut = 0
    let otmCall = 0
    let otmPut = 0

    for (const opt of options) {
        const strike = Number(opt.strike ?? opt.strikePrice ?? 0)
        if (atmStrike > 0 && (strike < minStrike || strike > maxStrike)) continue
        if (!includeAtm && strike === atmStrike) continue

        let callVal = 0
        let putVal = 0

        if (metric === 'Volume') {
            callVal = Number(opt.callVolume ?? opt.ce?.volume ?? opt.ce?.vol ?? 0)
            putVal = Number(opt.putVolume ?? opt.pe?.volume ?? opt.pe?.vol ?? 0)
        } else if (metric === 'OI') {
            callVal = Number(opt.callOpenInterest ?? opt.ce?.oi ?? opt.ce?.openInterest ?? 0)
            putVal = Number(opt.putOpenInterest ?? opt.pe?.oi ?? opt.pe?.openInterest ?? 0)
        } else if (metric === 'Chng in OI') {
            callVal = Number(opt.callOpenInterestChange ?? opt.callChange ?? opt.ce?.oiChange ?? 0)
            putVal = Number(opt.putOpenInterestChange ?? opt.putChange ?? opt.pe?.oiChange ?? 0)
        }

        if (strike < atmStrike) {
            itmCall += callVal
        } else {
            otmCall += callVal
        }

        if (strike > atmStrike) {
            itmPut += putVal
        } else {
            otmPut += putVal
        }
    }

    const itmNet = itmCall - itmPut
    const itmTotal = itmCall + itmPut
    const itmNetP = itmTotal > 0 ? (itmNet / itmTotal) * 100 : 0

    const otmNet = otmPut - otmCall
    const otmTotal = otmCall + otmPut
    const otmNetP = otmTotal > 0 ? (otmNet / otmTotal) * 100 : 0

    const finalNet = itmNet + otmNet
    const finalTotal = itmTotal + otmTotal
    const finalNetP = finalTotal > 0 ? (finalNet / finalTotal) * 100 : 0

    return {
        timeStr,
        timestamp: rawTime,
        isLive: false,
        isPD,
        dayType,
        atmStrike,
        itmCall,
        itmPut,
        itmNet,
        itmNetP,
        otmCall,
        otmPut,
        otmNet,
        otmNetP,
        finalNet,
        finalNetP,
        indexLtp: spotPrice,
    }
}

// ----------------------------------------------------------------------------
// Compute Above / Below Snapshot Metrics
// ----------------------------------------------------------------------------
function computeAboveBelowSnapshot(
    snap: any,
    symbol: string,
    expiry: string | undefined,
    strikeRange: number,
    includeAtm: boolean
): any {
    const rawTime = snap.timestamp || snap.timestampIST || snap.time || snap.TimeStamp || snap.datetime || snap.timeStr || Date.now()
    const isPD = !!snap?.isPD || (typeof snap?.time === 'string' && snap.time.startsWith('PD')) || snap?.dayType === 'PD'
    const dayType = snap?.dayType || (isPD ? 'PD' : 'CURRENT')
    let timeStr = extractTimeString(rawTime)
    if (isPD && !timeStr.startsWith('PD')) {
        timeStr = 'PD ' + timeStr
    }
    const options = extractOptionsFromSnap(snap, symbol, expiry)
    const spotPrice = extractSpotPriceFromSnap(snap, options, symbol)
    const step = getStrikeStep(symbol, options)

    let atmStrike = Number(snap.atmStrike || 0)
    if (atmStrike <= 0 && spotPrice > 0) {
        atmStrike = Math.round(spotPrice / step) * step
    }
    if (atmStrike <= 0 && options.length > 0) {
        atmStrike = Number(options[Math.floor(options.length / 2)]?.strike || options[0]?.strike || 0)
    }

    const activeRange = strikeRange || 10
    const minStrike = atmStrike > 0 ? atmStrike - activeRange * step : 0
    const maxStrike = atmStrike > 0 ? atmStrike + activeRange * step : Infinity

    let aboveCombOI = 0
    let aboveCombVol = 0
    let aboveCombOIChg = 0

    let belowCombOI = 0
    let belowCombVol = 0
    let belowCombOIChg = 0

    for (const opt of options) {
        const strike = Number(opt.strike ?? opt.strikePrice ?? 0)
        if (atmStrike > 0 && (strike < minStrike || strike > maxStrike)) continue

        const ceOI = Number(opt.callOpenInterest ?? opt.ce?.oi ?? opt.ce?.openInterest ?? 0)
        const peOI = Number(opt.putOpenInterest ?? opt.pe?.oi ?? opt.pe?.openInterest ?? 0)
        const combOI = ceOI + peOI

        const ceVol = Number(opt.callVolume ?? opt.ce?.volume ?? opt.ce?.vol ?? 0)
        const peVol = Number(opt.putVolume ?? opt.pe?.volume ?? opt.pe?.vol ?? 0)
        const combVol = ceVol + peVol

        const ceOIChg = Number(opt.callOpenInterestChange ?? opt.callChange ?? opt.ce?.oiChange ?? 0)
        const peOIChg = Number(opt.putOpenInterestChange ?? opt.putChange ?? opt.pe?.oiChange ?? 0)
        const combOIChg = ceOIChg + peOIChg

        if (strike > atmStrike) {
            aboveCombOI += combOI
            aboveCombVol += combVol
            aboveCombOIChg += combOIChg
        } else if (strike < atmStrike) {
            belowCombOI += combOI
            belowCombVol += combVol
            belowCombOIChg += combOIChg
        } else if (strike === atmStrike) {
            // Rule: If ATM ON, add ATM data to Below Strike! If ATM OFF, don't consider ATM.
            if (includeAtm) {
                belowCombOI += combOI
                belowCombVol += combVol
                belowCombOIChg += combOIChg
            }
        }
    }

    const netCombOI = belowCombOI - aboveCombOI
    const totalCombOI = belowCombOI + aboveCombOI
    const netCombOIP = totalCombOI > 0 ? (netCombOI / totalCombOI) * 100 : 0

    const netCombVol = belowCombVol - aboveCombVol
    const totalCombVol = belowCombVol + aboveCombVol
    const netCombVolP = totalCombVol > 0 ? (netCombVol / totalCombVol) * 100 : 0

    const netCombOIChg = belowCombOIChg - aboveCombOIChg
    const totalAbsOIChg = Math.abs(belowCombOIChg) + Math.abs(aboveCombOIChg)
    const netCombOIChgP = totalAbsOIChg > 0 ? (netCombOIChg / totalAbsOIChg) * 100 : 0

    return {
        timeStr,
        timestamp: rawTime,
        isLive: false,
        isPD,
        dayType,
        atmStrike,
        indexLtp: spotPrice,
        belowCombOI,
        aboveCombOI,
        netCombOI,
        netCombOIP,
        belowCombVol,
        aboveCombVol,
        netCombVol,
        netCombVolP,
        belowCombOIChg,
        aboveCombOIChg,
        netCombOIChg,
        netCombOIChgP,
    }
}
